import { HttpHeaders, HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, from, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { AnalyticsService } from 'src/app/analytics/analytics.service';
import { AuthService } from 'src/app/auth/auth.service';
import { SnackbarService } from 'src/app/shared/snackbar/snackbar.service';
import { environment } from 'src/environments/environment';
import { StoreStockLotExportItemResponse } from './store-stock-lots-export.model';
import { InventoryResponse, ProductStoreStock, StoreStockAvailable, StoreStockLot } from '../interfaces/stock.interface';
import { SaveLotDTO } from '../components/add-stock-manually-modal/models/add-stock-manually-model';
import { AnalyticsEvent } from 'src/app/analytics/models/analytics-event';
import { AvailableStockProduct } from './stock.service';
import { ProductListItem } from 'src/app/core/interfaces/products-types/tire-product.interface';


interface TransferLotRequest {
  storeId: number;
  stockQuantity: number;
}

// TODO: refactor this interfaces

interface BackendErrorEnvelope {
  error?: unknown;
  details?: unknown;
}

interface InsufficientStockError extends BackendErrorEnvelope {
  error?: 'Insufficient Stock';
  details?: {
    available?: number;
    requested?: number;
  };
}

export interface StockSearchResponse {
  items: ProductListItem[]
}

@Injectable({
  providedIn: 'root'
})
export class StoreStockLotsService {
  private readonly baseUrl = environment.cafler_backoffice_back_url;
  private readonly storeStockLotsUrl = `${this.baseUrl}/store-stock-lots`;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    private readonly snackbarService: SnackbarService,
    private readonly analytics: AnalyticsService

  ) { }

  private authRequest<T>(fn: (headers: HttpHeaders) => Observable<T>, errorMsg?: string): Observable<T> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) return throwError(() => new Error('No token available'));
        const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
        return fn(headers);
      }),
      catchError((err: unknown) => {
        if (errorMsg) this.snackbarService.showError(errorMsg);
        return throwError(() => err);
      })
    );
  }


  exportStoreStockLots(params?: { storeIds?: number[]; productIds?: number[]; searchText?: string }): Observable<StoreStockLotExportItemResponse> {
    // NOTE: searchText existe en la firma pero el backend actual no lo usa en el body (si lo soporta, se añade aquí).
    const body = this.buildExportStoreStockLotsBody(params);

    return this.authRequest(
      (headers) => this.http.post<StoreStockLotExportItemResponse>(`${this.storeStockLotsUrl}/tires/export`, body, { headers }),
      'Error al exportar el stock'
    );
  }

  getStockLotById(id: number): Observable<StoreStockLot> {
    return this.authRequest(
      (headers) => this.http.get<StoreStockLot>(`${this.storeStockLotsUrl}/${id}`, { headers }),
      'Error al cargar el lote'
    );
  }

  updateStockLot(request: StoreStockLot): Observable<StoreStockLot> {
    return this.authRequest(
      (headers) => this.http.put<StoreStockLot>(`${this.storeStockLotsUrl}/${request.lotId}`, request, { headers }),
      'Error al actualizar el lote de stock'
    ).pipe(tap(() => this.snackbarService.showSuccess('Lote de stock actualizado exitosamente')));
  }

  deleteStockLot(id: number): Observable<void> {
    return this.authRequest(
      (headers) => this.http.delete<void>(`${this.storeStockLotsUrl}/${id}`, { headers }),
      'Error al eliminar el lote de stock'
    ).pipe(tap(() => this.snackbarService.showSuccess('Lote de stock eliminado exitosamente')));
  }

  transferLot(lotId: number, storeId: number, quantity: number): Observable<void> {
    const body: TransferLotRequest = { storeId, stockQuantity: quantity };

    return this.authRequest((headers) => this.http.post<void>(`${this.storeStockLotsUrl}/${lotId}/transfer`, body, { headers }))
      .pipe(
        tap(() => this.snackbarService.showSuccess('Stock transferido exitosamente')),
        catchError((err: unknown) => {
          const details = this.tryReadInsufficientStockDetails(err);
          if (details) {
            this.snackbarService.showError(
              `Stock insuficiente. Disponible: ${details.available ?? 'N/A'}, Solicitado: ${details.requested ?? 'N/A'}`
            );
          } else {
            this.snackbarService.showError('Error al transferir el stock');
          }
          return throwError(() => err);
        })
      );
  }

  searchStock(
    storeId?: number,
    searchText?: string
  ): Observable<StockSearchResponse> {
    let params = new HttpParams()

    if (isFiniteNumber(storeId)) params = params.set('storeId', String(storeId));
    if (searchText && searchText.trim()) params = params.set('searchText', searchText.trim());

    return this.authRequest(
      (headers) => this.http.get<StockSearchResponse>(`${this.storeStockLotsUrl}/tires`, { headers, params }),
      'Error al cargar el stock'
    );
  }

  getTyreStockPaginated(
    page: number = 1,
    pageSize: number = 10,
    storeId?: number,
    searchText?: string
  ): Observable<InventoryResponse> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('pageSize', String(pageSize));

    if (isFiniteNumber(storeId)) params = params.set('storeId', String(storeId));
    if (searchText && searchText.trim()) params = params.set('searchText', searchText.trim());

    return this.authRequest(
      (headers) => this.http.get<InventoryResponse>(`${this.storeStockLotsUrl}/tires/paginated`, { headers, params }),
      'Error al cargar el stock'
    );
  }

  createStockLot(createLotDTO: SaveLotDTO): Observable<StoreStockLot> {
    return this.authRequest(
      (headers) => this.http.post<StoreStockLot>(`${this.storeStockLotsUrl}`, createLotDTO, { headers }),
      'Error al crear el lote'
    ).pipe(
      tap(() => {
        this.analytics.track(AnalyticsEvent.StockAddManualSuccess, {
          ...this.analytics.globalProps(),
          qty: createLotDTO.stockQuantity,
          product_id: createLotDTO.productId,
          price: createLotDTO.price,
          warehouse_id: createLotDTO.storeId,
        });
      }),
      catchError((err: HttpErrorResponse) => {
        const errorCode = err.error?.statusCode ?? 'UNKNOWN_ERROR';
        const errorMessage = err.error?.message ?? err.statusText ?? 'Unknown error';

        this.analytics.track(AnalyticsEvent.StockAddManualFail, {
          ...this.analytics.globalProps(),
          error_code: errorCode,
          error_message: errorMessage,
          http_status: err.status,
          product_id: createLotDTO.productId,
          warehouse_id: createLotDTO.storeId,

        });

        return throwError(() => err);
      })
    );
  }

  // getStoreStockLotsByProductId(productId: number, storeId?: number): Observable<StoreStockAvailable[]> {
  //   let params = new HttpParams();
  //   if (isFiniteNumber(storeId)) {
  //     params = params.set('storeId', String(storeId));
  //   }

  //   return this.authRequest(
  //     (headers) => this.http.get<StoreStockAvailable[]>(`${this.storeStockLotsUrl}/product/${productId}`, { headers, params }),
  //     'Error al cargar el stock'
  //   );
  // }

  getStoresByProductId(productId: number, storeId?: number): Observable<ProductStoreStock[]> {
    if (!productId || productId <= 0) {
      return of([]);
    }

    let params = new HttpParams();
    if (isFiniteNumber(storeId)) {
      params = params.set('storeId', String(storeId));
    }

    return this.authRequest(
      (headers) => this.http.get<ProductStoreStock[]>(`${this.baseUrl}/products/${productId}/stores`, { headers, params }),
      'Error al cargar el stock'
    );
  }

  getAvailableProducts(): Observable<AvailableStockProduct[]> {
    return this.authRequest((headers) => this.http.get<AvailableStockProduct[]>(`${this.storeStockLotsUrl}/available`, { headers }));
  }

  private tryReadInsufficientStockDetails(err: unknown): InsufficientStockError['details'] | null {
    const maybe = err as { error?: unknown } | null;
    const envelope = maybe?.error as InsufficientStockError | undefined;
    if (!envelope) return null;

    if (envelope.error === 'Insufficient Stock') {
      const details = envelope.details;
      if (details && typeof details === 'object') return details as InsufficientStockError['details'];
    }

    return null;
  }


  private buildExportStoreStockLotsBody(params?: {
    storeIds?: number[];
    productIds?: number[];
    searchText?: string;
  }): { storeId?: number[]; productId?: number[] } {
    const body: { storeId?: number[]; productId?: number[] } = {};

    const storeIds = params?.storeIds?.filter(isFiniteNumber) ?? [];
    const productIds = params?.productIds?.filter(isFiniteNumber) ?? [];

    if (storeIds.length > 0) body.storeId = storeIds;
    if (productIds.length > 0) body.productId = productIds;

    return body;
  }

}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

